import type { TFunction } from "i18next"
import { Expand } from "lucide-react"
import { Icon } from "@nexus/ui"
import { useTranslation } from "react-i18next"
import type { MyBrandCreativeCardModel } from "../lib/drilldown-card.types"
import { formatYmdDotted } from "../lib/drilldown-row"
import { DrilldownCreativeCardShell } from "./DrilldownCreativeCardShell"
import { DrilldownCardBadge } from "./DrilldownCardBadge"
import { DrilldownCardStatBig, DrilldownCardStatCompact } from "./DrilldownCardStatRow"

function trendAriaLabel(t: TFunction, percent: string | null | undefined): string | undefined {
  if (percent == null || percent.length === 0) return undefined
  return t("drilldown.card.trendVsAccount", "{{percent}} vs account average", { percent })
}

export interface DrilldownMyBrandCreativeCardProps {
  creative: MyBrandCreativeCardModel
  onOpenDrawer?: () => void
}

export function DrilldownMyBrandCreativeCard({ creative, onOpenDrawer }: DrilldownMyBrandCreativeCardProps) {
  const { t } = useTranslation()
  const [primaryKpi, ...compactKpis] = creative.kpis

  return (
    <DrilldownCreativeCardShell
      thumbnailUrl={creative.thumbnailUrl}
      assetUrl={creative.assetUrl}
      isVideo={creative.isVideo}
      isCarousel={false}
      alt={creative.objectTypeLabel}
      title={creative.objectTypeLabel}
      onStatsClick={onOpenDrawer}
      mediaOverlays={
        <>
          <div className="absolute left-1.5 top-1.5 flex items-center gap-1">
            <DrilldownCardBadge icon={creative.isVideo ? "clapperboard" : "image"}>
              {creative.isVideo ? t("common.mediaType.video", "Video") : t("common.mediaType.image", "Image")}
            </DrilldownCardBadge>
            {creative.dupCount !== null ? (
              <DrilldownCardBadge>{t("drilldown.card.dup", "{{count}} dup", { count: creative.dupCount })}</DrilldownCardBadge>
            ) : null}
          </div>

          <div className="absolute right-1.5 top-1.5 flex items-center gap-1">
            {onOpenDrawer ? (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  onOpenDrawer()
                }}
                aria-label={t("drilldown.myBrandDrawer.openDetails", "Open creative details")}
                className="flex size-8 items-center justify-center rounded-lg border border-border/60 bg-white/90 text-gray-700 opacity-0 shadow-sm transition-opacity hover:text-foreground group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:bg-black/60 dark:text-gray-200"
              >
                <Expand className="size-3.5" />
              </button>
            ) : null}
            <a
              href={creative.adLibUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              aria-label={t("common.openExternal")}
              className="flex size-6 items-center justify-center rounded-full bg-white/90 text-gray-700 transition-colors hover:text-foreground dark:bg-black/60 dark:text-gray-200"
            >
              <Icon name="external-link" size="xs" color="inherit" />
            </a>
          </div>

          {creative.isVideo ? (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center transition-opacity duration-200 group-hover:opacity-0">
              <span className="flex size-8 items-center justify-center rounded-full bg-black/40 text-white ring-1 ring-white/25 backdrop-blur-[2px]">
                <svg viewBox="0 0 24 24" fill="currentColor" className="size-4" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </span>
            </div>
          ) : null}

          {creative.daysActive !== null ? (
            <div className="absolute bottom-1.5 left-1.5">
              <DrilldownCardBadge icon="calendar">
                {t("drilldown.card.daysActive", "{{count}} days active", { count: creative.daysActive })}
              </DrilldownCardBadge>
            </div>
          ) : null}
        </>
      }
      statsChildren={
        <div className="flex flex-col gap-0.5 py-1.5">
          {primaryKpi ? (
            <DrilldownCardStatBig
              dotColor={primaryKpi.dotColor}
              label={primaryKpi.label}
              value={primaryKpi.value}
              trendLabel={primaryKpi.trendLabel}
              trendTone={primaryKpi.trendTone}
              trendDirection={primaryKpi.trendDirection}
              trendAriaLabel={trendAriaLabel(t, primaryKpi.trendLabel)}
            />
          ) : null}
          {compactKpis.map((kpi) => (
            <DrilldownCardStatCompact
              key={kpi.label}
              dotColor={kpi.dotColor}
              label={kpi.label}
              value={kpi.value}
              trendLabel={kpi.trendLabel}
              trendTone={kpi.trendTone}
              trendAriaLabel={trendAriaLabel(t, kpi.trendLabel)}
            />
          ))}
          <div className="flex items-center gap-1 px-2 pt-0.5">
            <Icon name="calendar" size="xs" color="inherit" className="text-muted-foreground" />
            <span className="text-[11px] text-muted-foreground">
              {t("drilldown.card.activeBetween", "Active between {{start}} – {{end}}", {
                start: formatYmdDotted(creative.minDate),
                end: formatYmdDotted(creative.maxDate),
              })}
            </span>
          </div>
        </div>
      }
    />
  )
}
